import * as React from 'react'
// libs
import { WrappedFieldArrayProps } from 'redux-form'
// hooks
import { useCurrentUser } from 'hooks/index'
// graphql
import { OWNED_BY_SELECT_QUERY } from 'graphql/plan/queries'
import { SECURITY_PROFILES_SELECT_QUERY } from 'graphql/securityProfiles/queries'
// types
import { IOption } from 'components/FormElements/Select'
import { GroupBase } from 'react-select'
import { QueryResult } from 'types/apollo'
import { DocumentNode } from 'graphql/language'
import { ISecurityProfilesQueryArgs, ISecurityProfilesResponse } from 'types/graphql'
// components
import { ReduxEntitySelect } from 'components/FormElements/ReduxForm'
// context
import { GroupSecurityContext } from 'containers/Security/components/GroupSecurity/layers/API'
// constants
import { SELECT_GROUP_LABELS } from 'constants/forms'
// themes
import { AddButton } from 'themes/views/security'
// views
import { CloseIcon, FieldsWrapper, TitleWrapper } from './views'
// utils
import { required } from 'utils/index'
import { securityProfileProps, userAndPositionProps } from 'components/FormElements/ReduxForm/ReduxEntitySelect/utils'

interface Props {
  query: DocumentNode
  usersIds: string[]
  variables?: Record<string, unknown>
  getOptions?: () => {}
  isRelatedForms?: boolean
}

export const renderUsers: React.FC<WrappedFieldArrayProps<any> & Props> = ({
  query = OWNED_BY_SELECT_QUERY,
  fields,
  usersIds,
  getOptions,
  variables = {},
  isRelatedForms
}) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const currentUser = useCurrentUser()

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { companyId = currentUser.companyId } = React.useContext(GroupSecurityContext)

  const userVariables = {
    userFilter: { companyId },
    positionFilter: { companyId },
    ...variables
  }

  const securityProfileVariables = {
    filter: { companyId }
  }

  const addField = () => fields.push({})

  const removeField = (idx: number) => () => fields.remove(idx)

  const prepareOptions = (options: GroupBase<IOption>[]) =>
    options.map((item) => {
      if (item.label === SELECT_GROUP_LABELS.USERS_AND_POSITIONS) {
        return {
          ...item,
          options: item.options.filter((option) => !usersIds.some((id) => id === option.value))
        }
      }

      return item
    })

  const getSecurityOptions = ({
    data
  }: {
    data: QueryResult<ISecurityProfilesResponse, ISecurityProfilesQueryArgs>
  }) => {
    const options = securityProfileProps.getOptions({ data })

    if (isRelatedForms) {
      const option = { value: 'inherit', label: 'Inherit security profiles' }

      return [option, ...options]
    }
    return options
  }

  const securityProfilesSearch = ({ search, refetch }) => refetch({ filter: { name: search, companyId } })

  return (
    <>
      <TitleWrapper>
        <div>These users:</div>
        <div>Assign Profile:</div>
      </TitleWrapper>

      {fields.map((field, idx) => (
        <FieldsWrapper key={field} data-testid='group-security-user-row' data-entity-id={idx}>
          <ReduxEntitySelect
            isGrouped
            query={query}
            validate={[required]}
            name={`${field}.user`}
            getOptions={getOptions}
            menuPortalTarget={null}
            variables={userVariables}
            prepareOptions={prepareOptions}
            handleSearch={userAndPositionProps.handleSearch}
          />
          <ReduxEntitySelect
            validate={[required]}
            menuPortalTarget={null}
            getOptions={getSecurityOptions}
            name={`${field}.securityProfile`}
            variables={securityProfileVariables}
            handleSearch={securityProfilesSearch}
            query={SECURITY_PROFILES_SELECT_QUERY}
          />
          <CloseIcon
            data-testid='group-security-user-remove'
            data-entity-id={idx}
            onClick={removeField(idx)}
          />
        </FieldsWrapper>
      ))}

      <AddButton data-testid='group-security-add-user' onClick={addField}>
        + add user
      </AddButton>
    </>
  )
}

export default renderUsers
